import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Trash2, Check, Plus } from "lucide-react";
import InstagramConnectionGuide from "./InstagramConnectionGuide";
import APIHelper from "./utils/APIHelper";

const PageSelector = ({ 
  selectedPage, 
  pages, 
  onPageSelect, 
  managedPages = [], 
  onManagedPagesUpdate,
  token 
}) => {
  const [updating, setUpdating] = useState(null);

  const handleToggleManage = async (pageId) => {
    setUpdating(pageId);
    try {
      const isManaged = managedPages.includes(pageId);
      
      if (isManaged) {
        await APIHelper.removeManagedPage(token, pageId);
        const newManagedPages = managedPages.filter(id => id !== pageId);
        if (onManagedPagesUpdate) {
          onManagedPagesUpdate(newManagedPages);
        }
      } else {
        await APIHelper.addManagedPage(token, pageId);
        const newManagedPages = [...managedPages, pageId];
        if (onManagedPagesUpdate) {
          onManagedPagesUpdate(newManagedPages);
        }
        // Auto-select the newly managed page
        onPageSelect(pageId);
      }
    } catch (error) {
      console.error("Error updating managed page:", error);
    } finally {
      setUpdating(null);
    }
  };

  const managedPagesList = pages.filter(p => managedPages.includes(p.id));
  const availablePagesList = pages.filter(p => !managedPages.includes(p.id));

  return (
    <div className="space-y-6">
      <div className="card-modern p-6">
        <Typography variant="h6" className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
          Select a page to complete your setup
        </Typography>
        <FormControl fullWidth variant="outlined" className="mb-4">
          <InputLabel className="text-gray-700 dark:text-gray-300">Select a Page</InputLabel>
          <Select
            value={selectedPage || ""}
            onChange={(e) => onPageSelect(e.target.value)}
            label="Select a Page"
            className="bg-white dark:bg-gray-800"
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {pages.map((page) => (
              <MenuItem key={page.id} value={page.id}>
                <Box className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${page.hasInstagram ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                    {page.hasInstagram ? 'Instagram' : 'Facebook'}
                  </span>
                  {page.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Managed Channels Section */}
      {managedPagesList.length > 0 && (
        <div className="card-modern p-6">
          <Typography variant="h6" className="font-bold text-gray-900 dark:text-white mb-4">
            Managed Channels ({managedPagesList.length})
          </Typography>
          <Box className="space-y-3">
            {managedPagesList.map((page) => (
              <Box
                key={page.id}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600"
              >
                <Box className="flex items-center gap-3 flex-1 min-w-0">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: page.hasInstagram ? "#E1306C" : "#1877F2",
                      flexShrink: 0,
                    }}
                  />
                  <Box className="flex-1 min-w-0">
                    <Typography
                      variant="body1"
                      className="font-semibold text-gray-900 dark:text-white truncate"
                    >
                      {page.name}
                    </Typography>
                    <Typography variant="caption" className="text-gray-600 dark:text-gray-400">
                      {page.hasInstagram ? "Instagram" : "Facebook"}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<Check size={14} />}
                    label="Managed"
                    color="success"
                    size="small"
                    className="ml-2"
                  />
                </Box>
                <Tooltip title="Remove from managed channels">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleToggleManage(page.id)}
                    disabled={updating === page.id}
                    className="ml-4"
                  >
                    {updating === page.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </div>
      )}

      {/* Available Channels Section */}
      {availablePagesList.length > 0 && (
        <div className="card-modern p-6">
          <Typography variant="h6" className="font-bold text-gray-900 dark:text-white mb-4">
            Available Channels ({availablePagesList.length})
          </Typography>
          <Box className="space-y-3">
            {availablePagesList.map((page) => (
              <Box
                key={page.id}
                className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
              >
                <Box className="flex items-center gap-3 flex-1 min-w-0">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: page.hasInstagram ? "#E1306C" : "#1877F2",
                      flexShrink: 0,
                    }}
                  />
                  <Box className="flex-1 min-w-0">
                    <Typography
                      variant="body1"
                      className="font-semibold text-gray-900 dark:text-white truncate"
                    >
                      {page.name}
                    </Typography>
                    <Typography variant="caption" className="text-gray-600 dark:text-gray-400">
                      {page.hasInstagram ? "Instagram" : "Facebook"}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={updating === page.id ? <CircularProgress size={14} /> : <Plus size={16} />}
                  onClick={() => handleToggleManage(page.id)}
                  disabled={updating === page.id}
                  className="ml-4 normal-case"
                >
                  Manage
                </Button>
              </Box>
            ))}
          </Box>
        </div>
      )}

      <InstagramConnectionGuide />
    </div>
  );
};

export default PageSelector;
